import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WORLDS } from "../data/content";
import { scrollToSection } from "../lib/scroll";
import Flower from "./Flower";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

const STEM = "#7C9A4E";

const nodePos = (i, total, radius = 41) => {
  const angle = (i / total) * Math.PI * 2 - Math.PI / 2;
  return { x: 50 + radius * Math.cos(angle), y: 50 + radius * Math.sin(angle) };
};

export default function HubOrbit() {
  const [active, setActive] = useState(WORLDS[0]);
  const activeIndex = WORLDS.findIndex((w) => w.id === active.id);

  return (
    <section id="hub" data-testid="hub-section" className="relative bg-[#2B2119] text-[#FBF3E4] py-24 md:py-32 overflow-hidden">
      <p className="absolute top-10 left-0 right-0 text-center font-display font-black uppercase text-[18vw] leading-none text-[#FBF3E4]/[0.05] pointer-events-none select-none" aria-hidden="true">
        BLOOM
      </p>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Navigation — 001 · everything is different, everything is connected"
          title={<>Everything starts <span className="italic text-en-pink">here.</span></>}
          note="pick a petal. every bloom leads home."
          noteColor="text-en-yellow"
        />

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          {/* the flower */}
          <Reveal className="relative mx-auto w-full max-w-[540px]">
            <div className="relative aspect-square">
              <div className="absolute inset-0 rounded-full border border-[#FBF3E4]/10" aria-hidden="true" />
              <div className="absolute inset-[9%] rounded-full border border-dashed border-[#7C9A4E]/40 animate-spin-slow" aria-hidden="true" />

              {/* wildflowers on the meadow ring */}
              <div className="absolute left-[4%] top-[52%] animate-floaty" style={{ "--r": "-10deg" }} aria-hidden="true">
                <Flower className="w-7 h-7" petals="#E85D9E" />
              </div>
              <div className="absolute right-[6%] top-[30%] animate-floaty" style={{ "--r": "8deg", animationDelay: "0.9s" }} aria-hidden="true">
                <Flower className="w-6 h-6" petals="#E3B341" center="#E85D9E" />
              </div>
              <div className="absolute right-[16%] bottom-[8%] animate-floaty" style={{ "--r": "-6deg", animationDelay: "1.6s" }} aria-hidden="true">
                <Flower className="w-8 h-8" petals="#4E9B94" center="#FBF3E4" />
              </div>

              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
                {WORLDS.map((w, i) => {
                  const p = nodePos(i, WORLDS.length);
                  return (
                    <motion.line
                      key={w.id}
                      x1="50"
                      y1="50"
                      x2={p.x}
                      y2={p.y}
                      stroke={STEM}
                      strokeWidth={active.id === w.id ? 1 : 0.5}
                      strokeDasharray="1.5 2.5"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.15 * i, ease: "easeOut" }}
                    />
                  );
                })}
              </svg>

              {/* daisy center */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-36 md:h-36 rounded-full bg-en-yellow shadow-[0_0_60px_rgba(227,179,65,0.4)] flex flex-col items-center justify-center text-center z-10">
                <div className="absolute inset-2 rounded-full border-2 border-dashed border-[#2A2118]/35" aria-hidden="true" />
                <span className="font-display font-black text-2xl text-[#2A2119] leading-none">E·N</span>
                <span className="font-mono text-[8px] tracking-[0.3em] uppercase text-[#2A2119]/60 mt-1">the hub</span>
              </div>

              {/* petals */}
              {WORLDS.map((w, i) => {
                const p = nodePos(i, WORLDS.length);
                const rot = (i / WORLDS.length) * 360;
                const isActive = active.id === w.id;
                return (
                  <button
                    key={w.id}
                    data-testid={`hub-node-${w.id}`}
                    onClick={() => setActive(w)}
                    aria-pressed={isActive}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group z-20"
                    style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  >
                    <span className={`block transition-transform duration-300 ${isActive ? "scale-110" : "group-hover:scale-110"}`}>
                      <span
                        className="flex items-center justify-center w-12 h-16 md:w-16 md:h-[5.25rem] transition-shadow duration-300"
                        style={{
                          backgroundColor: w.color,
                          borderRadius: "55% 55% 55% 55% / 72% 72% 34% 34%",
                          transform: `rotate(${rot}deg)`,
                          boxShadow: isActive ? `0 0 34px ${w.color}99` : "0 4px 14px rgba(0,0,0,0.35)",
                        }}
                      >
                        <span className="font-mono text-[10px] md:text-xs font-semibold text-white drop-shadow-md" style={{ transform: `rotate(${-rot}deg)` }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </span>
                    </span>
                    <span className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap font-mono text-[9px] md:text-[10px] tracking-[0.15em] uppercase transition-opacity ${isActive ? "opacity-100" : "opacity-50 group-hover:opacity-90"}`}>
                      {w.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* detail panel */}
          <div className="relative min-h-[22rem]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                data-testid="hub-detail-panel"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl border border-[#FBF3E4]/15 bg-[#FBF3E4]/[0.05] backdrop-blur-sm p-8 md:p-10"
              >
                <div className="flex items-center justify-between gap-4 mb-6">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FBF3E4]/50">
                    Petal {String(activeIndex + 1).padStart(2, "0")} / {String(WORLDS.length).padStart(2, "0")}
                  </p>
                  <span className="rounded-full px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase" style={{ backgroundColor: `${active.color}26`, color: active.color }}>
                    {active.status}
                  </span>
                </div>
                <h3 className="font-display font-black text-3xl md:text-5xl mb-5" style={{ color: active.color }}>
                  {active.name}
                </h3>
                <p className="text-[#FBF3E4]/70 leading-relaxed mb-7 max-w-md">{active.blurb}</p>
                <ul className="space-y-3 mb-9">
                  {active.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-[#FBF3E4]/80">
                      <span style={{ color: active.color }} aria-hidden="true">✦</span>
                      {it}
                    </li>
                  ))}
                </ul>
                {active.href ? (
                  <a
                    href={active.href}
                    data-testid="hub-enter-button"
                    onClick={scrollToSection(active.href)}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white transition-transform hover:scale-105"
                    style={{ backgroundColor: active.color }}
                  >
                    Enter {active.name}
                    <ArrowUpRight size={14} />
                  </a>
                ) : null}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
